import { logUsage } from 'owa-analytics';
import getFolderTable from '../selectors/getFolderTable';
import { orchestrator } from 'satcheljs';
import { updateFolderPermissionsInStore } from '../actions/publicActions';
import {
    updateFolderPermissions,
    updateFolderPermissionsFailed,
    updateFolderPermissionsSuccess,
} from '../actions/publicActions';
import { arePermissionArraysEqual } from '../util/compareFolderPermissions';
import Folder from 'owa-service/lib/contract/Folder';
import Permission from 'owa-service/lib/contract/Permission';
import updateFolderPermissionsService from '../services/updateFolderPermissions';
import { trace } from 'owa-trace';
import { getAggregationBucket } from 'owa-analytics-actions';

/**
 * An orchestrator that updates folder permissions on server.
 * @param actionMessage Object containing folderId and permissions
 */
export default orchestrator(updateFolderPermissions, async actionMessage => {
    const folderId: string = actionMessage.folderId;
    const permissions: Permission[] = actionMessage.permissions;
    // If folder permissions are not modified then we will return.
    const folder: Folder = getFolderTable().get(folderId);
    const savedPermissions = folder.PermissionSet?.Permissions;
    if (arePermissionArraysEqual(savedPermissions, permissions)) {
        updateFolderPermissionsSuccess();
        return;
    }

    let responseMessage;
    try {
        responseMessage = await updateFolderPermissionsService(folderId, {
            Permissions: permissions,
        });
    } catch (error) {
        updateFolderPermissionsFailed();
        trace.error('Update folder permission on server call failed with error ' + error);
        return;
    }

    // Update folder permission in store if it is succesfully updated on server
    if (responseMessage && responseMessage.ResponseClass == 'Success') {
        updateFolderPermissionsInStore(folderId, permissions);
        updateFolderPermissionsSuccess();
        // Log folder permissions.
        logFolderPermissions(permissions);
    } else {
        updateFolderPermissionsFailed();
        trace.error('Update folder permission on server call failed');
    }
});

/**
 * Log folder permissions details.
 * @param permissions Array of permissions
 */
function logFolderPermissions(permissions: Permission[]) {
    const permissionsCount = permissions.length;
    const permissionLevelCounts = {}; // Variable to store count of permission levels in folder permissions.
    let defaultPermission; // Default permission. Note: Permissions assigned to Default and anonymous users can not be deleted.
    let anonymousPermission; // Anonymous permission.
    permissions.forEach(permission => {
        const { PermissionLevel, UserId } = permission;
        if (UserId.DistinguishedUser === 'Default') {
            defaultPermission = permission;
        } else if (UserId.DistinguishedUser === 'Anonymous') {
            anonymousPermission = permission;
        }
        if (!permissionLevelCounts[PermissionLevel]) {
            permissionLevelCounts[PermissionLevel] = 0;
        }
        permissionLevelCounts[PermissionLevel] += 1;
    });
    logUsage('Triage_FolderPermissions_Edit', {
        ...{
            owa_1: getAggregationBucket({ value: permissionsCount }),
            owa_2: defaultPermission.PermissionLevel,
            owa_3: anonymousPermission.PermissionLevel,
        },
        ...permissionLevelCounts,
    });
}
